import React, { useEffect, useRef } from "react";

import {
  VENTURE_FORMATION_GROUP_SHELL_KEYS,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS,
} from "@frontend/common";

import { DealsSection } from "@/types/deals.types";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import TableCreatorDragButton from "@FillInTableContainer/shared/components/TableCreatorDragButton/TableCreatorDragButton";
import { TableCreatorShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import { isAddDeleteSubGroupShell } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";

import DealTermsMergeShell from "./components/DealTermsMergeShell/DealTermsMergeShell";
import SubGroupShellDividerPlusBar from "./components/SubGroupShellDividerPlusBar/SubGroupShellDividerPlusBar";
import * as S from "./DealTermsMergeSubGroupShell.style";
import { useTableCreatorVentureFormationSubGroupShell } from "./hooks/useTableCreatorVentureFormationSubGroupShell";
import VfAssetTransferShell from "../../../VfAssetTransferShell/VfAssetTransferShell";

export interface PositionInfoProps {
  groupTableKey: DealsSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  tableKey: string | null;
  tableIndex: number;
  subGroupShellKey: string | null;
  subGroupShellIndex: number;
  subGroupShellCount: number;
}

interface Props extends PositionInfoProps {
  subGroupShellCount: number;
  shellList: TableCreatorShellList;
  isFocusedGroupShell: boolean;
  isClearGroupShell: boolean;
  isDeleteGroupShell: boolean;
  isAlertGroupShell: boolean;
  isClearGroupShellSection?: boolean;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: (groupTableKey: string) => void;
  handleDragOn: () => void;
}

// NOTE: 한 줄 씩 클릭되도록
const isFocusSubGroupShellGroupShellKeyList: string[] = [
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.initialInvestment,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.contingentInvestment,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.disclosedPaymentsSumVf,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.totalInvestmentVf,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.ownershipStructureDetails,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.otherFinancialTerms,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.licensorVf,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.licenseeVf,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.assetTransferRow,
  "Advisor",
];

// NOTE: 여러개 추가되는 항목들좌측 드래그 가능하도록
const isDragSubGroupShellList: string[] = [
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.contingentInvestment,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.licensorVf,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.licenseeVf,
  VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.assetTransferRow,
  "Advisor",
];

const DealTermsMergeSubGroupShell = ({
  groupShellIndex,
  groupShellKey,
  groupTableIndex,
  groupTableKey,
  tableIndex,
  tableKey,
  subGroupShellCount,
  subGroupShellIndex,
  subGroupShellKey,
  shellList,
  isAlertGroupShell,
  isClearGroupShell,
  isDeleteGroupShell,
  isFocusedGroupShell,
  isClearGroupShellSection,
  handleDragEnd,
  handleDragOn,
  handleDragStart,
}: Props) => {
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellKey,
    subGroupShellIndex,
    tableKey,
    tableIndex,
  };

  const articleSection = useArticleSection();
  const selectButtonRef = useRef<HTMLDivElement>(null);

  const isPossibleSelected =
    !!groupShellKey &&
    isFocusSubGroupShellGroupShellKeyList.includes(subGroupShellKey || "");

  const {
    focusStatus,
    isFocused,
    isEnableEdit,
    isClearSubGroupShell,
    isDeleteSubGroupShell,
    isLastSubGroupShell,
    isFocusedAddButton,
    handleClickToDeleteSubGroupShell,
    handleFocusSubGroupShell,
    clickAddSubGroupShellButton,
    handleDefaultFocus,
    changeFocusStatus,
    changePositionInfo,
    changePositionInfoToDefault,
    keydownClearOrDeleteSubGroupShellData,
  } = useTableCreatorVentureFormationSubGroupShell({
    positionPropsInfo,
    subGroupShellCount,
  });

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;
        const clickSelectButton = selectButtonRef.current?.contains(target);

        if (clickSelectButton) return;
        if (target.dataset.button) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isFocused]);

  const changeFocusStatusForDragging = () => {
    changePositionInfo();
    changeFocusStatus({
      type: "dragging",
      container: "subGroupShell",
    });
  };

  const changeFocusStatusForDragEnd = () => {
    changePositionInfoToDefault();
    changeFocusStatus({
      type: "default",
      container: "noFocus",
    });
  };

  const isDisplayClearDeleteButton =
    (isClearSubGroupShell || isDeleteSubGroupShell) && isEnableEdit;

  // Asset Transfer 행은 LA&SC Rights Scope 처럼 add 바를 absolute 로 띄워(flow 공간 0)
  // 행이 밀착되도록 한다.
  const isAssetTransferRow =
    subGroupShellKey ===
    VENTURE_FORMATION_SUB_GROUP_SHELL_KEYS.assetTransferRow;

  // Asset Transfer 행은 LA&SC 와 동일하게 행이 1개여도 드래그 버튼 영역을 항상 렌더해
  // 헤더 행과 바디 행의 정렬(좌측 들여쓰기)이 일정하도록 한다.
  // 실제 드래그 동작 자체는 아래 draggable / 핸들러에서 subGroupShellCount > 1 로 제한.
  const isDraggingSubGroupShell =
    isEnableEdit &&
    isDragSubGroupShellList.includes(subGroupShellKey || "") &&
    (isAssetTransferRow || subGroupShellCount > 1);
  const assetTransferTopDividerStyle = isAssetTransferRow
    ? ({
        width: "calc(100% - 0.4rem)",
        position: "absolute",
        left: "0.4rem",
        top: "-0.4rem",
      } as const)
    : undefined;
  const assetTransferBottomDividerStyle = isAssetTransferRow
    ? ({
        width: "calc(100% - 0.4rem)",
        position: "absolute",
        left: "0.4rem",
        top: "auto",
        bottom: "-0rem",
      } as const)
    : undefined;

  return (
    <>
      {isAddDeleteSubGroupShell(articleSection, subGroupShellKey) && (
        <SubGroupShellDividerPlusBar
          dividerType={isAssetTransferRow ? "TABLE_ROW" : undefined}
          style={assetTransferTopDividerStyle}
          onClick={(event) => {
            clickAddSubGroupShellButton(event, {
              tableIndexParameter: tableIndex,
              groupShellIndexParameter: groupShellIndex,
              groupShellKeyParameter: groupShellKey,
              subGroupShellIndexParameter: subGroupShellIndex,
              subGroupShellKeyParameter: subGroupShellKey,
            });
          }}
          isLastPosition={false}
          isVisibility={isFocusedAddButton}
          subGroupShellKey={subGroupShellKey}
        />
      )}
      <S.GroupShellOutsideWrapper
        onKeyDown={keydownClearOrDeleteSubGroupShellData}
      >
        {isDisplayClearDeleteButton && (
          <S.DeleteTableButtonWrapper $isDragging={isDraggingSubGroupShell}>
            <S.DeleteTableButton
              data-button="delete-button"
              onClick={handleClickToDeleteSubGroupShell}
            >
              <img src={IconXMark} alt="button for clear Row" />
              {focusStatus.type === "clear" ? `Clear` : null}
              {focusStatus.type === "delete" ? `Delete` : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        )}
        <S.GroupShellWrapper
          isFocused={isFocused}
          isDragging={isFocused && focusStatus.type === "dragging"}
        >
          <S.SelectWrapper isPossibleSelected={isPossibleSelected}>
            <S.GroupShellSelectButtonDividerWrapper
              ref={selectButtonRef}
              onClick={handleFocusSubGroupShell}
              isPossibleSelected={isPossibleSelected}
            >
              <S.GroupShellSelectButtonDivider className="select-button divider" />
              <S.GroupShellSelectButton className="select-button button">
                SELECT
              </S.GroupShellSelectButton>
            </S.GroupShellSelectButtonDividerWrapper>
            {isDraggingSubGroupShell && (
              <S.DragButton
                data-button="drag-button"
                // 행이 1개인 Asset Transfer 는 드래그 버튼을 숨기되(visibility:hidden)
                // 2.4rem 공간은 유지해 헤더-바디 정렬을 맞춘다.
                style={
                  isAssetTransferRow && !(subGroupShellCount > 1)
                    ? { visibility: "hidden" }
                    : undefined
                }
                onClick={(e) => {
                  e.stopPropagation();
                  changeFocusStatusForDragging();
                }}
                onDragStart={(e) => {
                  if (!(subGroupShellCount > 1)) return;
                  handleDragStart(e, subGroupShellIndex);
                  changeFocusStatusForDragging();
                }}
                onDragEnd={() => {
                  if (!(subGroupShellCount > 1)) return;
                  if (changeFocusStatusForDragEnd) {
                    handleDragEnd(subGroupShellKey || "");
                    changeFocusStatusForDragEnd();
                  }
                }}
                onDrag={() => {
                  if (!(subGroupShellCount > 1)) return;
                  handleDragOn();
                }}
                draggable={subGroupShellCount > 1}
              >
                <TableCreatorDragButton
                  dragContainer="subGroupShell"
                  isDragging={isFocused && focusStatus.type === "dragging"}
                  onClick={(e) => {
                    e.stopPropagation();
                    changeFocusStatusForDragging();
                  }}
                />
              </S.DragButton>
            )}
            <S.GroupShell
              $noWrap={
                groupShellKey === VENTURE_FORMATION_GROUP_SHELL_KEYS.rightsScope
              }
            >
              {shellList.map(({ shellKey }, shellListIndex) =>
                groupShellKey ===
                VENTURE_FORMATION_GROUP_SHELL_KEYS.rightsScope ? (
                  // Asset Transfer(rights scope) 셀은 LA&SC Rights Scope 셀과
                  // 동일한 컴포넌트(VfAssetTransferShell)로 렌더한다.
                  <VfAssetTransferShell
                    key={`shellList-${String(shellListIndex)}`}
                    groupTableKey={groupTableKey}
                    groupTableIndex={groupTableIndex}
                    groupShellKey={groupShellKey}
                    // Asset Transfer 행은 subGroupShell 모델이지만, 공유 read/write
                    // 경로는 행 인덱스를 groupShellIndex 로 사용하므로 행 인덱스를 전달.
                    groupShellIndex={subGroupShellIndex}
                    subGroupShellIndex={subGroupShellIndex}
                    subGroupShellKey={subGroupShellKey}
                    shellKey={shellKey}
                    shellIndex={0}
                    tableKey={tableKey}
                    tableIndex={tableIndex}
                    isFocusedGroupShell={isFocusedGroupShell}
                    isClearGroupShell={isClearGroupShell}
                    isDeleteGroupShell={isDeleteGroupShell}
                    isAlertGroupShell={isAlertGroupShell}
                    isClearSubGroupShell={isClearSubGroupShell}
                    isDeleteSubGroupShell={isDeleteSubGroupShell}
                    isFocusGroupShellContainer={!!isClearGroupShellSection}
                  />
                ) : (
                  <DealTermsMergeShell
                    key={`shellList-${String(shellListIndex)}`}
                    groupTableKey={groupTableKey}
                    groupTableIndex={groupTableIndex}
                    groupShellKey={groupShellKey}
                    groupShellIndex={groupShellIndex}
                    subGroupShellIndex={subGroupShellIndex}
                    subGroupShellKey={subGroupShellKey}
                    shellKey={shellKey}
                    shellIndex={0}
                    tableKey={tableKey}
                    tableIndex={tableIndex}
                    isFocusedGroupShell={isFocusedGroupShell}
                    isClearGroupShell={isClearGroupShell}
                    isDeleteGroupShell={isDeleteGroupShell}
                    isAlertGroupShell={isAlertGroupShell}
                    isClearSubGroupShell={isClearSubGroupShell}
                    isDeleteSubGroupShell={isDeleteSubGroupShell}
                    isClearGroupShellSection={isClearGroupShellSection}
                  />
                ),
              )}
            </S.GroupShell>
          </S.SelectWrapper>
        </S.GroupShellWrapper>
      </S.GroupShellOutsideWrapper>
      {isAddDeleteSubGroupShell(articleSection, subGroupShellKey) &&
        isLastSubGroupShell && (
          <SubGroupShellDividerPlusBar
            dividerType={isAssetTransferRow ? "TABLE_ROW" : undefined}
            style={assetTransferBottomDividerStyle}
            onClick={(event) => {
              clickAddSubGroupShellButton(event, {
                tableIndexParameter: tableIndex,
                groupShellIndexParameter: groupShellIndex,
                groupShellKeyParameter: groupShellKey,
                subGroupShellIndexParameter: subGroupShellIndex + 1,
                subGroupShellKeyParameter: subGroupShellKey,
              });
            }}
            isLastPosition
            isVisibility={isFocusedAddButton}
            subGroupShellKey={subGroupShellKey}
          />
        )}
    </>
  );
};

export default DealTermsMergeSubGroupShell;
